/**
 * Central brand identity.
 *
 * Third-party service configuration (ads, Turnstile, analytics) lives in
 * `public-config.ts`, not here, because this module is imported by the private room and
 * anything in it reaches the room's JavaScript bundle.
 *
 * Every brand string in the product resolves through this file: page titles, canonicals,
 * social cards, the wordmark, the footer, contact addresses. Nothing else hard-codes a
 * name, a domain or an address.
 *
 * "The Cap" was the internal codename during specification and appears nowhere in this
 * application — not in metadata, structured data, URLs, social cards, analytics, the
 * database or any component.
 *
 * Renaming the product means changing the defaults below, or overriding them at build
 * time, plus the long-form content. It must never require a change to product logic.
 *
 * Build-time overrides (e.g. in `.env.production`, or a staging build):
 *   VITE_SITE_NAME, VITE_SITE_SHORT_NAME, VITE_SITE_DOMAIN, VITE_SITE_ORIGIN,
 *   VITE_SITE_DESCRIPTION, VITE_SOCIAL_TITLE, VITE_SOCIAL_DESCRIPTION,
 *   VITE_CONTACT_SUPPORT, VITE_CONTACT_PRIVACY, VITE_LEGAL_ENTITY,
 *   VITE_TURNSTILE_SITE_KEY, VITE_WEB_ANALYTICS_TOKEN, VITE_AD_CLIENT_ID
 */

/**
 * Every override is read as a *static* `import.meta.env.VITE_X` expression.
 *
 * This matters more than it looks. Vite substitutes these at build time by textual
 * replacement, so a dynamic lookup like `env[key]` is never substituted — it silently
 * resolves to `undefined` in a production bundle and every override falls back to its
 * default without any error. That bug shipped once here and was caught only because the
 * Turnstile site key was missing from the built JavaScript.
 */
function value(override: string | undefined, fallback: string): string {
  return override !== undefined && override !== '' ? override : fallback;
}

const DEFAULT_NAME = 'Budget Cap';
const DEFAULT_DOMAIN = 'budgetcap.org';
const DEFAULT_CONTACT = 'helpdesk@budgetcap.org';
const DEFAULT_MAKER = 'Global Anomaly';

export interface BrandConfig {
  readonly siteName: string;
  readonly siteShortName: string;
  readonly siteDomain: string;
  readonly siteOrigin: string;
  readonly siteDescription: string;
  readonly socialTitle: string;
  readonly socialDescription: string;
  readonly contactSupport: string;
  readonly contactPrivacy: string;
  /** Who is behind the site. Shown in the footer and named on the privacy page. */
  readonly legalEntity: string;
}

const siteName = value(import.meta.env.VITE_SITE_NAME, DEFAULT_NAME);
const siteDomain = value(import.meta.env.VITE_SITE_DOMAIN, DEFAULT_DOMAIN);

export const BRAND: BrandConfig = {
  siteName,
  siteShortName: value(import.meta.env.VITE_SITE_SHORT_NAME, siteName),
  siteDomain,
  siteOrigin: value(import.meta.env.VITE_SITE_ORIGIN, `https://${siteDomain}`),
  siteDescription: value(
    import.meta.env.VITE_SITE_DESCRIPTION,
    'Agree on a group spending limit without anyone sharing their own. One private number each, one group-safe cap, no accounts.',
  ),
  socialTitle: value(
    import.meta.env.VITE_SOCIAL_TITLE,
    'Agree on a budget without anyone sharing theirs.',
  ),
  socialDescription: value(
    import.meta.env.VITE_SOCIAL_DESCRIPTION,
    'Create a private link. Everyone enters the most they’d feel comfortable spending. The group only sees one rounded, group-safe cap.',
  ),
  contactSupport: value(import.meta.env.VITE_CONTACT_SUPPORT, DEFAULT_CONTACT),
  contactPrivacy: value(import.meta.env.VITE_CONTACT_PRIVACY, DEFAULT_CONTACT),
  legalEntity: value(import.meta.env.VITE_LEGAL_ENTITY, DEFAULT_MAKER),
};

/** Absolute URL for a public path, for canonicals and social cards. */
export function absoluteUrl(path: string): string {
  return new URL(path, BRAND.siteOrigin).toString();
}
